(ns hai.ui.components.collection.query
  "Events for fetching collection data from Pathom resolvers written to utilise
  `hai.ui.components.collection.pathom-utils`"
  (:require
   [edn-query-language.core :as eql]
   [hai.entity.helpers :as entity]
   [hai.ui.components.collection.core :as-alias core]
   [hai.ui.components.collection.filters :as filters]
   [hai.ui.components.collection.pagination :as pagination]
   [hai.ui.components.collection.utils :as utils]
   [hai.ui.components.form.core :as form]
   [hai.ui.rf-sugar :refer [>e defevent-fx defsub]]
   [humbleai.belt.core :refer [meta-merge]]
   [humbleai.belt.malli.transform.default-value :as default-value]
   [humbleai.belt.malli.transform.translate-keys :as translate-keys]
   [medley.core :refer [assoc-some]]))

(defn find-path
  [query]
  (let [needle :collection/items
        search (fn search
                 [{:keys [children type key]} path]
                 (if (= key needle)
                   path
                   (cond
                     (= :prop type)
                     nil

                     (= :join type)
                     (some (fn [v]
                             (search v (conj path key)))
                           children)

                     (= :root type)
                     (some (fn [v]
                             (search v path))
                           children))))]
    ;; We're either searching for a join key such as `:collection/items` (e.g., `{:assistants/table
    ;; [{:collection/items [...]}]}`) or the collection's resolver is queried as an attribute (e.g.,
    ;; `:assistants/table`) as a direct child of the AST root node.
    (or (search (eql/query->ast query) [])
        [(-> query eql/query->ast :children first :key)])))

(defn mask-ast
  [{:keys [children] :as source-ast} mask-ast]
  (reduce
   (fn [ast {mask-children :children
             :keys [key]
             :as mask-node}]
     ;; NOTE Removes the entire join key
     ;;FIXME broken
     (if-let [source-node (->> children (remove (comp #{key} :key)) first)]
       (if (and (seq (:children source-node)) (seq mask-children))
         (update ast :children conj (mask-ast source-node mask-node))
         (update ast :children conj source-node))
       ast))
   (assoc source-ast :children [])
   (:children mask-ast)))

(def QueryCollection
  [:map
   [:collection/type
    {:default :query}
    [:= :query]]
   [:collection.query/error
    {:optional true}
    :any]
   [:collection.query/query
    {:optional true
     ::translate-keys/from :query}
    [:fn
     {:error/message "`:collection.query/query` must be a valid EQL query and contain a single child (attribute)"}
     (fn [?query]
       (and (entity/query? ?query)
            (-> (eql/query->ast ?query)
                (mask-ast (eql/query->ast [:total]))
                :children
                (count)
                (= 1))))]]
   [:collection.query/results-path
    {::default-value/fn (comp find-path :collection.query/query)}
    vector?]
   [:collection.query/headers
    {:optional true
     ::translate-keys/from :headers}
    [:map-of :string :string]]
   [:collection.query/on-load-success
    {:optional true
     ::translate-keys/from :on-load-success}
    ifn?]
   [:settings/schema
    {:optional true}
    :any]
   [:settings/render
    {:optional true}
    ifn?]])

(defsub :collection.query/error
  [?table
   table <- [:table ?table]]
  (get table :collection.query/error))

(def filter-subquery-ast (eql/query->ast [:filter-values]))

(defn table->query
  "Given a table entity build an EQL query"
  [db ?table]
  (let [{:as table
         :table/keys [id paginate? pagination]
         :collection.query/keys [results-path query]}
        (utils/->table db ?table)

        {:table/keys [search filters settings]}
        (form/db->current-value db id)

        {:table/keys [cursor direction per-page sort-attrs]}
        pagination

        pagination
        (assoc-some {}
                    :direction direction
                    :per-page per-page
                    :attrs sort-attrs
                    :cursor cursor)

        filters
        (-> {}
            (into (remove (comp nil? second)) filters)
            (not-empty))

        params
        (assoc-some {:pagination pagination}
                    :settings settings
                    :search search
                    :collection.filters/selected filters)

        resolver-prop
        (last results-path)]
    (->> (eql/query->ast query)
         (eql/transduce-children
          (map (fn [node]
                 (if (= resolver-prop (:key node))
                   (cond-> node
                     paginate? (eql/merge-asts pagination/subquery-ast)
                     (filters/query-for-filters? table) (eql/merge-asts filter-subquery-ast)
                     ;; meta-merge (not shallow merge) so a base :collection.filters/selected
                     ;; declared on the query node (e.g. the tasks board's {:task/open? true})
                     ;; COMPOSES with the form's :table/filters rather than being replaced
                     ;; wholesale. Only collections with a base selection differ; a colliding
                     ;; key can opt back into replace via ^:replace metadata.
                     :always (update :params meta-merge params))
                   node))))
         (eql/ast->query))))

(defevent-fx :table/resolve
  [{:keys [db]} ?table {:keys [on-success] :as options}]
  "Given a table entity or an ID of a table in the app db, build the table's EQL query, resolve it
  and populate the results into the table's app-db state."
  (let [{:as table
         :table/keys [id]
         :collection.query/keys [on-load-success results-path headers]}
        (utils/->table db ?table)

        on-success
        (fn [response]
          (let [error
                (:com.wsscode.pathom3.error/error-message response)

                {:keys [cursor next-cursor prev-cursor total filter-values]
                 :collection/keys [items]
                 :collection.filters/keys [options]}
                (get-in response results-path)

                table
                (assoc-some
                 {:table/id id
                  :table/rows items
                  :table/pagination {:table/cursor cursor
                                     :table/prev-cursor prev-cursor
                                     :table/next-cursor next-cursor
                                     :table/total total}}
                 :table/filter-values filter-values
                 ;; The composable-filter path's per-dimension options + counts,
                 ;; present only under :user/unified-relationships? ON. assoc-some
                 ;; keeps OFF collections (and non-opted-in resolvers) untouched.
                 :collection.filters/options options
                 :collection.query/error error)]
            (when error (js/console.error (str "Pathom request error: " error)))
            (>e [::core/setup table])
            ;; TODO `on-success` is really `on-response`... it does not mean that there was
            ;; no pathom error.
            ;; IMPORTANT (evgheni): on-success may expect the new data be available in the app-db
            ;;                      so it should be executed after [::core/setup table]
            (when on-success
              (on-success table))

            (when on-load-success
              (on-load-success table))))]
    {:fx [[:dispatch [:table/assoc table :table/resolving? true]]
          [:dispatch [:resolve (table->query db table)
                      (cond-> (assoc options
                                     :on-success on-success
                                     :on-success-fx [[:dispatch [:table/assoc table :table/resolving? false]]])
                        headers (assoc :headers headers))]]]}))

(defn- reinit-rows-fx
  "Effects map that pushes `rows` into a collection as an optimistic update: write
  the `[:collections id :table/rows]` slice and re-init the form the views read
  from (`:table/form -> :form/fields -> :table/rows -> :form.field/current-value`).
  Mirrors what `core/::setup` does on every resolve, so the change renders without
  a server round-trip. Pair the calling event with `[:table/resolve id]` to
  reconcile against server truth (and to self-heal on error).

  Returns `nil` (a no-op) when the collection isn't mounted: without this, an
  unmounted `tid` would write a phantom `[:collections tid]` entry holding only
  the optimistic rows. This makes every optimistic lever below mount-tolerant, so
  a cross-surface caller (e.g. task creation, which can fire on a page where the
  target board isn't rendered) needn't guard the call site."
  [db tid rows]
  (when (get-in db [:collections tid])
    {:db (assoc-in db [:collections tid :table/rows] rows)
     :fx [[:dispatch [:form/re-init tid (assoc (form/db->current-value db tid)
                                               :table/rows rows)]]]}))

(defevent-fx :table/patch-row
  [{:keys [db]} ?table id-key id patch]
  "Optimistically merge `patch` into the row whose `id-key` equals `id`. Generic
  instant-feedback lever for a `:query` collection; see `reinit-rows-fx`."
  (let [tid (utils/->id ?table)
        rows (mapv (fn [row]
                     (if (= id (get row id-key)) (merge row patch) row))
                   (:table/rows (form/db->current-value db tid)))]
    (reinit-rows-fx db tid rows)))

(defevent-fx :table/remove-row
  [{:keys [db]} ?table id-key id]
  "Optimistically drop the row whose `id-key` equals `id`. Generic instant-feedback
  lever for a `:query` collection; see `reinit-rows-fx`."
  (let [tid (utils/->id ?table)
        rows (filterv (fn [row] (not= id (get row id-key)))
                      (:table/rows (form/db->current-value db tid)))]
    (reinit-rows-fx db tid rows)))

(defevent-fx :table/add-row
  [{:keys [db]} ?table row]
  "Optimistically prepend `row` to a `:query` collection's rows. Generic
  instant-feedback lever; see `reinit-rows-fx`. Pair with a re-resolve to
  reconcile against server truth. Unlike `:table/patch-row`/`:table/remove-row`
  - dispatched from within a mounted board - this fires on *creation*, which can
  originate from a surface where the target board isn't rendered (mobile add
  sheet, universal bar); the `reinit-rows-fx` mount guard makes that a no-op."
  (let [tid (utils/->id ?table)]
    (reinit-rows-fx db tid (into [row] (:table/rows (form/db->current-value db tid))))))

;; TEMPORARY SHIM ([HAI-001-10]). `:table/resolve` throws "No table found" when
;; the target isn't in `:collections` (via `utils/->table`). That throw is an
;; artifact of resolves historically being dispatched only from *within* a
;; mounted collection - a consequence of development sequencing, not a designed
;; contract. A cross-surface caller (e.g. task creation from the shared add
;; modal, which fires on pages where a given board isn't rendered) needs a
;; resolve that no-ops when its target isn't mounted, *without* every such
;; caller hand-rolling a `[:collections id]` guard. The proper fix is to make
;; `:table/resolve` itself tolerant of an unmounted target, but that reshapes a
;; primitive used across ~25 namespaces and belongs in its own scoped change.
;; Until then this names the tolerance in one place rather than fanning the
;; responsibility out to all callers.
(defevent-fx :table/resolve-if-mounted
  [{:keys [db]} ?table options]
  "Like `:table/resolve`, but a no-op when the target collection isn't mounted.
  See the shim note above."
  (when (get-in db [:collections (utils/->id ?table)])
    {:dispatch [:table/resolve ?table options]}))

(defevent-fx :table/debounce-resolve
  [_ ?table options]
  {:dispatch [:table/assoc ?table :table/resolving? true]
   :debounce/dispatch {:id ::on-search
                       :event [:table/resolve ?table options]
                       :duration 500}})

(defevent-fx :table/debounce-stop
  [_ ?table _]
  {:dispatch [:table/assoc ?table :table/resolving? false]
   :debounce/stop {:id ::on-search}})
